import React, { Component, ErrorInfo, ReactNode } from 'react';

import { Button, Text } from '@chakra-ui/react';
import { Alert } from '@firehydrant/design-system';
import { isChunkLoadError } from 'helpers/lazyWithPreload';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Returns true for chunk load errors — either our typed ChunkLoadError, or
 * native browser dynamic import error messages that escape the retry wrapper
 * (e.g., in environments where our retryLoader doesn't intercept the failure).
 */
function isChunkError(error: Error): boolean {
  if (isChunkLoadError(error)) return true;
  const msg = error.message ?? '';
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Error boundary that intercepts chunk loading failures and shows a targeted
 * "please reload" UI instead of the generic error screen.
 *
 * Chunk load errors are CDN/deployment noise, not actionable bugs — this
 * boundary explicitly does NOT report them to Bugsnag. Non-chunk errors are
 * rethrown from render() so the parent Bugsnag ErrorBoundary can catch them.
 *
 * Must be a class component because React error boundaries require
 * getDerivedStateFromError / componentDidCatch lifecycle methods.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkError(error)) {
      console.warn('[ChunkErrorBoundary] Caught chunk load error:', error.message);
    }
    // Non-chunk errors: no Bugsnag call here — the parent ErrorBoundary handles it.
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    if (isChunkError(error)) {
      return <ChunkErrorFallback />;
    }

    // Non-chunk error: rethrow from render() so the parent Bugsnag ErrorBoundary
    // can catch and report it. Throwing from render() propagates to the next
    // error boundary up the tree.
    throw error;
  }
}

function ChunkErrorFallback() {
  const handleReload = () => {
    try {
      sessionStorage.removeItem('vite-failed-chunks');
    } catch (_e) {
      // sessionStorage may be unavailable; proceed with reload anyway
    }
    window.location.reload();
  };

  return (
    <Alert variant="info">
      <Text as="p" fontWeight="bold">
        FireHydrant was just updated.
      </Text>
      <Text as="p">Please reload the page to get the latest version.</Text>
      <Button colorScheme="blue" mt={3} onClick={handleReload}>
        Reload now
      </Button>
    </Alert>
  );
}
